/**
 * The event brand, as it appears in the logo artwork. Distinct from `event.name`
 * in the database, which is per-season editable copy — this is the fixed mark,
 * used for image alt text and email branding.
 */
export const BRAND_NAME = 'Julian Alps Trail Run';

export const POST_TYPES = ['trasa', 'parkirišče', 'štartno-ciljni'] as const;
export type PostType = (typeof POST_TYPES)[number];

export const POST_STATUSES = ['draft', 'active', 'deleted'] as const;
export type PostStatus = (typeof POST_STATUSES)[number];

export const SHIFT_STATES = ['open', 'proposed', 'assigned', 'cancelled', 'done'] as const;
export type ShiftState = (typeof SHIFT_STATES)[number];

/** Per-person, per-schedule-version confirmation status. */
export const CONFIRMATION_STATUSES = ['potrjen', 'čaka', 'zavrnjen'] as const;
export type ConfirmationStatus = (typeof CONFIRMATION_STATUSES)[number];

/**
 * How a `briefing_send` row came about (SPEC §1.1F). Both kinds pin what the
 * volunteer sees; only `mail` means we actually wrote to them, which is what
 * keeps a change notice from being somebody's first ever email.
 */
export const BRIEFING_SEND_KINDS = ['mail', 'silent'] as const;
export type BriefingSendKind = (typeof BRIEFING_SEND_KINDS)[number];

export const LANGS = ['sl', 'en'] as const;
export type Lang = (typeof LANGS)[number];

/** Volunteer profile enums (from the signup Google Sheet; lenient — '' = unknown). */
export const GENDERS = ['moški', 'ženski'] as const;
export type Gender = (typeof GENDERS)[number];

export const EXPERIENCE_LEVELS = ['neizkušen(a)', 'izkušen(a)', 'zelo izkušen(a)'] as const;
export type ExperienceLevel = (typeof EXPERIENCE_LEVELS)[number];

/**
 * The same assessment reduced to what scheduling compares, ordered best first.
 * `neznano` sits between the two assessed extremes on purpose: an unassessed
 * volunteer is thinner ground than an experienced one but better than someone
 * the coordinators looked at and called inexperienced.
 */
export const EXPERIENCE_TIERS = ['zelo-izkušen', 'izkušen', 'neznano', 'neizkušen'] as const;
export type ExperienceTier = (typeof EXPERIENCE_TIERS)[number];

export const EXPERIENCE_TIER_LABEL: Record<ExperienceTier, string> = {
  'zelo-izkušen': 'zelo izkušen(a)',
  izkušen: 'izkušen(a)',
  neznano: 'brez ocene izkušenosti',
  neizkušen: 'neizkušen(a)',
};

/**
 * Read the free-text `volunteer.experience` as a tier. The column is lenient —
 * the app writes an `EXPERIENCE_LEVELS` value, the signup sheet writes whatever
 * the coordinators typed — so matching is diacritic- and case-tolerant and
 * anything unrecognised reads as `neznano` rather than as inexperience.
 *
 * Any "neizkušen" anywhere in the text settles it first, and only then is
 * "zelo" read as an intensifier: tested the other way round, "zelo neizkušen"
 * would come back as the *top* tier, which is the one mistake here that cannot
 * be allowed to happen quietly.
 */
export function experienceTier(raw: string): ExperienceTier {
  const v = raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (v.includes('neizkusen') || v.includes('ni izkusen')) return 'neizkušen';
  if (/^zelo izkusen/.test(v)) return 'zelo-izkušen';
  if (v.startsWith('izkusen')) return 'izkušen';
  return 'neznano';
}

/**
 * Rank of a tier, best first — what the urgent-post preference sorts on.
 * Anything off the list reads as `neznano`, never as -1: sorting ahead of
 * `zelo izkušen(a)` is the one direction an unrecognised value must not fail in.
 */
export function experienceRank(tier: ExperienceTier): number {
  const i = EXPERIENCE_TIERS.indexOf(tier);
  return i === -1 ? EXPERIENCE_TIERS.indexOf('neznano') : i;
}

/** Assessed as experienced: the people an urgent post is staffed from first. */
export function isExperienced(tier: ExperienceTier): boolean {
  return tier === 'zelo-izkušen' || tier === 'izkušen';
}

/** Availability per time slot, as collected in the signup sheet. */
export const AVAILABILITY_VALUES = ['na-voljo', 'delno', 'ni-na-voljo', 'neznano'] as const;
export type AvailabilityValue = (typeof AVAILABILITY_VALUES)[number];

export const AVAILABILITY_VALUE_LABEL: Record<AvailabilityValue, string> = {
  'na-voljo': 'na voljo',
  delno: 'na voljo, ne v celoti',
  'ni-na-voljo': 'ni na voljo',
  neznano: 'nimamo podatka',
};

export interface AvailabilitySlot {
  /** Stable key stored in volunteer.availability. */
  id: string;
  /** Event-day key (d0 = petek, d1 = sobota, d2 = nedelja). */
  dayKey: 'd0' | 'd1' | 'd2';
  /** Short slot name matching the sheet column, e.g. "zjutraj". */
  name: string;
  /** Hour range label, e.g. "5h–8h". */
  hours: string;
  /** Same range as minutes from midnight — what scheduling compares against. */
  fromMin: number;
  toMin: number;
  /** Sheet column prefix used to match CSV headers (day part). */
  sheetDay: 'PET' | 'SOB' | 'NED';
}

/** The 12 signup-sheet availability slots (PET 4× · SOB 5× · NED 3×). */
export const AVAILABILITY_SLOTS: AvailabilitySlot[] = [
  { id: 'd0-05-08', dayKey: 'd0', name: 'zjutraj', hours: '5h–8h', sheetDay: 'PET', fromMin: 300, toMin: 480 },
  { id: 'd0-12-16', dayKey: 'd0', name: 'zgodaj popoldne', hours: '12h–16h', sheetDay: 'PET', fromMin: 720, toMin: 960 },
  { id: 'd0-16-20', dayKey: 'd0', name: 'pozno popoldne', hours: '16h–20h', sheetDay: 'PET', fromMin: 960, toMin: 1200 },
  { id: 'd0-20-24', dayKey: 'd0', name: 'zvečer', hours: '20h–24h', sheetDay: 'PET', fromMin: 1200, toMin: 1440 },
  { id: 'd1-05-08', dayKey: 'd1', name: 'zjutraj', hours: '5h–8h', sheetDay: 'SOB', fromMin: 300, toMin: 480 },
  { id: 'd1-08-12', dayKey: 'd1', name: 'dopoldne', hours: '8h–12h', sheetDay: 'SOB', fromMin: 480, toMin: 720 },
  { id: 'd1-12-16', dayKey: 'd1', name: 'zgodaj popoldne', hours: '12h–16h', sheetDay: 'SOB', fromMin: 720, toMin: 960 },
  { id: 'd1-16-20', dayKey: 'd1', name: 'pozno popoldne', hours: '16h–20h', sheetDay: 'SOB', fromMin: 960, toMin: 1200 },
  { id: 'd1-20-24', dayKey: 'd1', name: 'zvečer', hours: '20h–24h', sheetDay: 'SOB', fromMin: 1200, toMin: 1440 },
  { id: 'd2-05-08', dayKey: 'd2', name: 'zjutraj', hours: '5h–8h', sheetDay: 'NED', fromMin: 300, toMin: 480 },
  { id: 'd2-08-12', dayKey: 'd2', name: 'dopoldne', hours: '8h–12h', sheetDay: 'NED', fromMin: 480, toMin: 720 },
  { id: 'd2-12-16', dayKey: 'd2', name: 'zgodaj popoldne', hours: '12h–16h', sheetDay: 'NED', fromMin: 720, toMin: 960 },
];

export const TAG_SCOPES = ['shared', 'private'] as const;
export type TagScope = (typeof TAG_SCOPES)[number];

/**
 * What a tag labels. The two vocabularies never mix: a volunteer tag is never
 * offered for a post, nor the other way round, and a tag's kind never changes.
 */
export const TAG_KINDS = ['volunteer', 'post'] as const;
export type TagKind = (typeof TAG_KINDS)[number];

/**
 * Who a coordinator login is. `admin` and `coordinator` are the marshal team and
 * may do everything; `organizer` is the race organisation, which reads the plan
 * and writes nothing but notes (SPEC §1.1L). What each may do is `access.ts`.
 */
export const COORDINATOR_ROLES = ['admin', 'coordinator', 'organizer'] as const;
export type CoordinatorRole = (typeof COORDINATOR_ROLES)[number];

export const MESSAGE_DIRECTIONS = ['in', 'out', 'note'] as const;
export const MESSAGE_SOURCES = ['imap', 'manual', 'app'] as const;
/** What the model or a coordinator says a message does: wait for an answer, or not (`commsTurn`). */
export const REPLY_CLASS_VALUES = ['asks', 'closes'] as const;

/** Scheduling rules (SPEC §3, decided). */
export const MIN_GAP_MINUTES = 60;
export const LONG_DAY_MINUTES = 10 * 60;
/** The shortest izmena worth drawing, and the step the board snaps a drag to. */
export const MIN_SHIFT_MINUTES = 15;

/**
 * Does this shift put somebody at the post — i.e. does it cover its window?
 *
 * Both halves are needed and each has been forgotten on its own: a row left
 * `open` while carrying a person is drawn by the board and counted as a hole by
 * coverage, and a row marked `assigned` with nobody on it does the reverse. A
 * `cancelled` row ("Odpoved") keeps its person too, and stands nobody at the
 * post. The predicate had grown five spellings across the app; this is the only
 * one.
 */
export function staffs(s: { volunteerId: string | null; state: string }): boolean {
  return s.volunteerId !== null && s.state !== 'open' && s.state !== 'cancelled';
}

/**
 * Would a publication carry this shift? The briefing and every mail read the
 * same two states, so the surfaces that ask "is there anything to publish"
 * have to read them as well (SPEC §1.1F).
 */
export function publishable(s: { state: string }): boolean {
  return s.state === 'assigned' || s.state === 'done';
}

export const TIMEZONE = 'Europe/Ljubljana';

/** Live mode (SPEC §1.1G). */
export const CHECKIN_KINDS = ['in', 'out'] as const;
export type CheckinKind = (typeof CHECKIN_KINDS)[number];

export const CHECKIN_BY = ['self', 'org'] as const;
export type CheckinBy = (typeof CHECKIN_BY)[number];

/** Problem-report categories on the briefing day-of view (stable keys; labels in UI copy). */
export const PROBLEM_CATEGORIES = ['zamenjava', 'oprema', 'promet', 'poskodba', 'drugo'] as const;
export type ProblemCategory = (typeof PROBLEM_CATEGORIES)[number];

/**
 * Handover event kinds (Predaje screen): a swap between two people, the same
 * person continuing, a post opening (first shift) and a post closing.
 */
export const HANDOVER_KINDS = ['predaja', 'nadaljevanje', 'prevzem', 'zaključek'] as const;
export type HandoverKind = (typeof HANDOVER_KINDS)[number];

/** Late-warning escalation steps in minutes after shift start ("Zamuja / ni prijave"). */
export const LATE_WARN_STEPS = [15, 30] as const;
/** Live panel: shifts starting within this many minutes are "starting soon". */
export const STARTING_SOON_MINUTES = 45;
/** Live panel shows the next N handovers. */
export const LIVE_HANDOVER_COUNT = 6;

/** AI draft presets (Prostovoljci → Komunikacija → Osnutek odgovora). */
export const AI_DRAFT_KINDS = ['reply', 'chase', 'custom'] as const;
export type AiDraftKind = (typeof AI_DRAFT_KINDS)[number];

export const AI_DRAFT_KIND_LABEL: Record<AiDraftKind, string> = {
  reply: 'Odgovori na zadnje sporočilo',
  chase: 'Opomnik za manjkajočo potrditev',
  custom: 'Po lastnem navodilu spodaj',
};

// ---------------------------------------------------------------------------
// Post photos (SPEC §1.1D, §1.3)
//
// The browser downscales and re-encodes before upload, so the server stores two
// derivatives per photo and never the original: `full` for the lightbox, `thumb`
// for the briefing filmstrip and the map panel. The caps below are what the
// upload endpoint enforces — the pipeline lowers encoder quality until it fits.
// ---------------------------------------------------------------------------

/** Photos one post can carry. Enough for an approach, a stance and a hazard. */
export const MAX_POST_PHOTOS = 8;

/** Longest side of the two stored derivatives, in pixels. */
export const PHOTO_FULL_MAX_PX = 1600;
export const PHOTO_THUMB_MAX_PX = 640;

export const MAX_PHOTO_FULL_BYTES = 900 * 1024;
export const MAX_PHOTO_THUMB_BYTES = 150 * 1024;

export const PHOTO_CAPTION_MAX = 160;

/**
 * What the store accepts. The web pipeline only ever produces WebP; JPEG and
 * PNG are here so fixtures and tests can insert bytes without an encoder.
 */
export const PHOTO_MIMES = ['image/webp', 'image/jpeg', 'image/png'] as const;
export type PhotoMime = (typeof PHOTO_MIMES)[number];

export const PHOTO_VARIANTS = ['full', 'thumb'] as const;
export type PhotoVariant = (typeof PHOTO_VARIANTS)[number];

/**
 * Where a photo's bytes live. The key is a capability — unguessable, stable for
 * the life of the row — so an `<img>` can load it without a session and the
 * response can be cached forever.
 */
export function photoUrl(key: string, variant: PhotoVariant = 'thumb'): string {
  return `/api/v1/photos/${key}/${variant}`;
}

/**
 * Scale `w`×`h` down so its longest side is at most `max`, keeping the aspect
 * ratio. Never scales up: an image already smaller than the cap is returned as
 * it is, so a 900 px photo does not get blurred into a 1600 px one.
 */
export function fitWithin(w: number, h: number, max: number): { width: number; height: number } {
  const longest = Math.max(w, h);
  if (longest <= max) return { width: Math.round(w), height: Math.round(h) };
  const ratio = max / longest;
  return {
    width: Math.max(1, Math.round(w * ratio)),
    height: Math.max(1, Math.round(h * ratio)),
  };
}

// ---------------------------------------------------------------------------
// External teams (SPEC §1.1J)
//
// A group's people are not individuals we know — they are numbered places in
// that group. The label is derived rather than typed, so renaming the group
// renames all of them and nobody has to keep a numbering scheme in their head.
// ---------------------------------------------------------------------------

/**
 * Is this row a place in an external team rather than someone we brief?
 *
 * The one spelling of the question. The exclusion is opt-in at every call site,
 * so a surface that spells the test itself is a surface that can forget to ask —
 * ask through here instead. On the board, `rosterPool()` answers the related
 * question of who this app may *offer*.
 */
export function isExternal(v: { externalGroupId: string | null }): boolean {
  return v.externalGroupId !== null;
}

/**
 * Has this person withdrawn from the event — "Ne pride" (SPEC §1.1K)?
 *
 * The one spelling of the question, like {@link isExternal}. The person stays
 * active — their thread, their link and a way back all remain — so the flag has
 * to be asked wherever people are planned, counted, mailed or offered. The
 * parameter keeps `cancelledAt` required so a query that forgot to select the
 * column fails to compile instead of answering "no".
 */
export function isCancelled(v: { cancelledAt: Date | string | null }): boolean {
  return (v.cancelledAt ?? null) !== null;
}

/**
 * How far behind what we have told a volunteer their schedule is (SPEC §1.1F).
 *
 * The briefing renders the last publication, so the coordinator surfaces have
 * to say when the plan and the page disagree — a tint on the person's row, a
 * badge on the roster, the counts on Pregled. One spelling, like
 * {@link isExternal}, because three surfaces asking it three ways is three
 * chances to answer differently.
 *
 * External teams are outside the lifecycle entirely; filter them before asking.
 */
export type PublicationState = 'objavljeno' | 'neobjavljeno' | 'brez objave' | 'brez izmen';

export function publicationState(v: {
  scheduleVersion: number;
  /** Version at the newest `briefing_send`, null when never published. */
  briefingSentVersion: number | null;
  /**
   * Shifts a publication would actually carry — {@link publishable}, not any
   * row at all. A proposal is a coordinator's draft, so somebody holding only
   * those would otherwise be reported as awaiting a publication the endpoint
   * then refuses with *brez izmen*.
   */
  hasShifts: boolean;
}): PublicationState {
  // The pin is asked first. Somebody published to and then emptied out still
  // has yesterday's shifts on their phone, and that is the one case where a
  // volunteer turns up to a shift nobody expects — exactly the case a shift
  // count of zero would have quietly excused.
  if (v.briefingSentVersion === null) {
    // Nothing to publish is not the same as failing to publish.
    return v.hasShifts ? 'brez objave' : 'brez izmen';
  }
  // The version moves on a material change and a publication carries the
  // version it published, so a mismatch is exactly an unpublished change.
  return v.briefingSentVersion === v.scheduleVersion ? 'objavljeno' : 'neobjavljeno';
}

/** Does this volunteer's page differ from the coordinator's plan? */
export function isUnpublished(state: PublicationState): boolean {
  return state === 'neobjavljeno' || state === 'brez objave';
}

/** Display name of one external-team slot: "PGD Bled" + 2 → "PGD Bled 2". */
export function externalSlotLabel(groupName: string, index: number): string {
  return `${groupName.trim()} ${index}`;
}
